/**
 * LifeMapView — Full life graph display as a hierarchical tree.
 *
 * Fetches the complete life graph from the chat-engine, builds a tree
 * using edges to determine parent-child relationships, and highlights
 * items that match the current conversation's items_summary.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
 Star, Target, CheckSquare, ListTodo, AlertTriangle,
 Lightbulb, Repeat, User, Users, Loader2, RefreshCw,
 ChevronDown, ChevronRight, Zap, Heart,
} from 'lucide-react'
import type { ComposedResponse, HierarchyItem } from '../../services/hortonV3Service'
import { BRAND } from '../../config/brand'

// ── Types ────────────────────────────────────────────────────

interface GraphNode {
 id: string
 type: string
 title: string
 description: string | null
 status: string
 confidence: number
 source: string
 icon: string
 color: string
 created_at: string | null
 updated_at: string | null
 metadata?: Record<string, any>
}

interface GraphEdge {
 id: string
 from_node_id: string
 to_node_id: string
 relation_type: string
 confidence: number
 source: string
 metadata?: Record<string, any>
}

interface LifeGraphResponse {
 user_id: string
 flat: { nodes: GraphNode[]; edges: GraphEdge[] }
}

interface TreeNode extends GraphNode {
 children: TreeNode[]
}

// ── Constants ────────────────────────────────────────────────

// Edge types where from=child, to=parent
const FROM_CHILD_TO_PARENT = new Set(['part_of', 'supports', 'blocks'])
// Edge types where from=parent, to=child
const FROM_PARENT_TO_CHILD = new Set(['toward', 'owns'])

const TYPE_META: Record<string, { label: string; icon: React.ReactNode; color: string; bgHighlight: string; tagBg: string }> = {
 identity: { label: 'Identity', icon: <User className="w-4 h-4" />, color: 'text-info', bgHighlight: 'bg-tint-info-fill border-tint-info-edge ', tagBg: 'bg-tint-info-solid text-info ' },
 person: { label: 'Person', icon: <Users className="w-4 h-4" />, color: 'text-info', bgHighlight: 'bg-tint-info-fill border-tint-info-edge ', tagBg: 'bg-tint-info-solid text-info ' },
 dream: { label: 'Dream', icon: <Star className="w-4 h-4" />, color: 'text-warn', bgHighlight: 'bg-tint-warn-fill border-tint-warn-edge ', tagBg: 'bg-tint-warn-solid text-warn ' },
 aspiration: { label: 'Aspiration', icon: <Star className="w-4 h-4" />, color: 'text-info', bgHighlight: 'bg-tint-info-fill border-tint-info-edge ', tagBg: 'bg-tint-info-solid text-info ' },
 goal: { label: 'Goal', icon: <Target className="w-4 h-4" />, color: 'text-brand', bgHighlight: 'bg-brand-subtle border-tint-brand-edge ', tagBg: 'bg-tint-brand-solid text-brand ' },
 skill: { label: 'Skill', icon: <Lightbulb className="w-4 h-4" />, color: 'text-info', bgHighlight: 'bg-tint-info-fill border-tint-info-edge', tagBg: 'bg-tint-info-solid text-info' },
 habit: { label: 'Habit', icon: <Repeat className="w-4 h-4" />, color: 'text-ok', bgHighlight: 'bg-tint-ok-fill border-tint-ok-edge', tagBg: 'bg-tint-ok-solid text-ok' },
 task: { label: 'Task', icon: <ListTodo className="w-4 h-4" />, color: 'text-ok', bgHighlight: 'bg-tint-ok-fill border-tint-ok-edge ', tagBg: 'bg-tint-ok-solid text-ok ' },
 action: { label: 'Action', icon: <Zap className="w-4 h-4" />, color: 'text-warn', bgHighlight: 'bg-tint-warn-fill border-tint-warn-edge ', tagBg: 'bg-tint-warn-solid text-warn ' },
 roadblock: { label: 'Roadblock', icon: <AlertTriangle className="w-4 h-4" />, color: 'text-warn', bgHighlight: 'bg-tint-warn-fill border-tint-warn-edge ', tagBg: 'bg-tint-warn-solid text-warn ' },
 value: { label: 'Value', icon: <Heart className="w-4 h-4" />, color: 'text-bad', bgHighlight: 'bg-tint-bad-fill border-tint-bad-edge', tagBg: 'bg-tint-bad-solid text-bad' },
}

const DEFAULT_META = { label: 'Item', icon: <CheckSquare className="w-4 h-4" />, color: 'text-muted', bgHighlight: 'bg-surface-alt border-strong', tagBg: 'bg-surface-alt text-primary ' }

/**
 * Statuses that mean "saved and live", so the status pill stays off.
 *
 * The pill is there to flag something the user should look at — parked, on hold, waiting
 * on information. `commitment` is none of those: `active_status.py` excludes it from
 * INACTIVE_ADMISSION_STATUSES on purpose, because a committed dream is a durable
 * aspiration that belongs on life-map surfaces and is merely not a schedulable
 * today-item. Now that saas-core's hierarchy is merged into this pane, most rows carry
 * it — badging them all would put a warning on a healthy map.
 */
const LIVE_STATUSES = new Set(['active', 'commitment'])

// ── Helpers ──────────────────────────────────────────────────

async function fetchLifeGraph(): Promise<LifeGraphResponse> {
 const resp = await fetch('/api/horton-v3/life-graph', { credentials: 'include' })
 if (!resp.ok) throw new Error(`Failed to fetch: ${resp.status}`)
 return resp.json()
}

function buildTree(nodes: GraphNode[], edges: GraphEdge[]): TreeNode[] {
 const nodeMap = new Map<string, TreeNode>()
 for (const n of nodes) {
 nodeMap.set(n.id, { ...n, children: [] })
 }

 const childIds = new Set<string>()

 for (const edge of edges) {
 let parentId: string
 let childId: string

 if (FROM_CHILD_TO_PARENT.has(edge.relation_type)) {
 childId = edge.from_node_id
 parentId = edge.to_node_id
 } else if (FROM_PARENT_TO_CHILD.has(edge.relation_type)) {
 parentId = edge.from_node_id
 childId = edge.to_node_id
 } else {
 continue
 }

 const parentNode = nodeMap.get(parentId)
 const childNode = nodeMap.get(childId)
 if (parentNode && childNode) {
 parentNode.children.push(childNode)
 childIds.add(childId)
 }
 }

 // Roots = nodes that are never a child
 const roots: TreeNode[] = []
 for (const [id, node] of nodeMap) {
 if (!childIds.has(id)) {
 roots.push(node)
 }
 }

 return roots
}

// ── Component ───────────────────────────────────────────────

interface LifeMapViewProps {
 composedResponse?: ComposedResponse
 className?: string
}

export function LifeMapView({ composedResponse, className = '' }: LifeMapViewProps) {
 const [loading, setLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [nodes, setNodes] = useState<GraphNode[]>([])
 const [edges, setEdges] = useState<GraphEdge[]>([])
 const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

 const load = useCallback(async () => {
 setLoading(true)
 setError(null)
 try {
 const data = await fetchLifeGraph()
 setNodes(data.flat.nodes)
 setEdges(data.flat.edges || [])
 // Auto-expand all nodes on first load
 const allIds = new Set(data.flat.nodes.map(n => n.id))
 setExpandedNodes(allIds)
 } catch (e: any) {
 setError(e.message || 'Failed to load life map')
 } finally {
 setLoading(false)
 }
 }, [])

 useEffect(() => { load() }, [load])

 // Build set of current-conversation item titles for highlighting
 const currentItemKeys = useMemo(() => {
 const keys = new Set<string>()
 const items = composedResponse?.items_summary || []
 for (const item of items) {
 if (item.title) {
 keys.add(`${(item.type || '').toLowerCase()}:${item.title.toLowerCase()}`)
 }
 }
 return keys
 }, [composedResponse])

 // Proposed items not yet in the life graph (new from this conversation)
 const proposedOnlyItems = useMemo(() => {
 const items = composedResponse?.items_summary || []
 const existingKeys = new Set(
 nodes.map(n => `${n.type.toLowerCase()}:${n.title.toLowerCase()}`)
 )
 return items.filter(item => {
 const key = `${(item.type || '').toLowerCase()}:${(item.title || '').toLowerCase()}`
 return !existingKeys.has(key)
 })
 }, [composedResponse, nodes])

 // Build the tree from nodes + edges
 const treeRoots = useMemo(() => buildTree(nodes, edges), [nodes, edges])

 // Build proposed items into a mini-tree using parent references
 const proposedTreeRoots = useMemo(() => {
 if (!proposedOnlyItems.length) return []
 const itemMap = new Map<string, any>()
 const roots: any[] = []
 for (const item of proposedOnlyItems) {
 const key = `${(item.type || '').toLowerCase()}:${(item.title || '').toLowerCase()}`
 itemMap.set(key, { ...item, _proposedChildren: [] })
 }
 for (const item of proposedOnlyItems as Array<HierarchyItem & { parent?: { type?: string; title?: string } }>) {
 const node = itemMap.get(`${(item.type || '').toLowerCase()}:${(item.title || '').toLowerCase()}`)!
 if (item.parent) {
 const parentKey = `${(item.parent.type || '').toLowerCase()}:${(item.parent.title || '').toLowerCase()}`
 const parent = itemMap.get(parentKey)
 if (parent) {
 parent._proposedChildren.push(node)
 } else {
 roots.push(node)
 }
 } else {
 roots.push(node)
 }
 }
 return roots
 }, [proposedOnlyItems])

 const toggleNode = (id: string) => {
 setExpandedNodes(prev => {
 const next = new Set(prev)
 if (next.has(id)) next.delete(id)
 else next.add(id)
 return next
 })
 }

 const isHighlighted = (node: GraphNode) => {
 return currentItemKeys.has(`${node.type.toLowerCase()}:${node.title.toLowerCase()}`)
 }

 // Recursive tree node renderer
 const renderTreeNode = (node: TreeNode, depth: number = 0) => {
 const meta = TYPE_META[node.type] || DEFAULT_META
 const highlighted = isHighlighted(node)
 const hasChildren = node.children.length > 0
 const isExpanded = expandedNodes.has(node.id)
 const indent = depth * 20

 return (
 <div key={node.id} style={{ marginLeft: `${indent}px` }}>
 <div className={`flex items-start gap-2 px-3 py-2 rounded-lg mb-1 ${
 highlighted
 ? `${meta.bgHighlight} border border-l-4`
 : 'hover:bg-surface-alt/50'
 }`}>
 {/* Expand/collapse */}
 {hasChildren ? (
 <button onClick={() => toggleNode(node.id)} className="mt-0.5 flex-shrink-0">
 {isExpanded
 ? <ChevronDown className="w-4 h-4 text-muted" />
 : <ChevronRight className="w-4 h-4 text-muted" />}
 </button>
 ) : (
 <span className="w-4 flex-shrink-0" />
 )}

 {/* Icon */}
 <span className={`mt-0.5 flex-shrink-0 ${meta.color}`}>{meta.icon}</span>

 {/* Content */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="text-sm font-medium text-primary truncate">
 {node.title}
 </span>
 <span className={`px-1.5 py-0.5 rounded text-app-micro font-semibold uppercase tracking-wide ${meta.tagBg}`}>
 {meta.label}
 </span>
 {highlighted && (
 <span className="text-app-micro px-1.5 py-0.5 rounded-full bg-info text-on-brand font-medium">
 in chat
 </span>
 )}
 {node.status && !LIVE_STATUSES.has(node.status) && (
 <span className="text-app-micro px-1.5 py-0.5 rounded-full bg-surface-alt text-secondary">
 {node.status}
 </span>
 )}
 </div>
 {node.description && (
 <p className="text-xs text-muted mt-0.5 line-clamp-1">
 {node.description}
 </p>
 )}
 </div>
 </div>

 {/* Children */}
 {hasChildren && isExpanded && (
 <div className="border-l border-soft ml-5">
 {node.children.map(child => renderTreeNode(child, depth + 1))}
 </div>
 )}
 </div>
 )
 }

 // Render proposed items (not yet in DB) as a mini-tree
 const renderProposedNode = (item: any, depth: number = 0) => {
 const type = (item.type || 'goal').toLowerCase()
 const meta = TYPE_META[type] || DEFAULT_META
 const hasChildren = item._proposedChildren && item._proposedChildren.length > 0
 const indent = depth * 20

 return (
 <div key={`proposed-${item.title}-${depth}`} style={{ marginLeft: `${indent}px` }}>
 <div className={`flex items-start gap-2 px-3 py-2 rounded-lg mb-1 border border-dashed ${meta.bgHighlight} opacity-80`}>
 {hasChildren ? (
 <ChevronDown className="w-4 h-4 text-muted mt-0.5 flex-shrink-0" />
 ) : (
 <span className="w-4 flex-shrink-0" />
 )}
 <span className={`mt-0.5 flex-shrink-0 ${meta.color}`}>{meta.icon}</span>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="text-sm font-medium text-primary truncate">
 {item.title}
 </span>
 <span className={`px-1.5 py-0.5 rounded text-app-micro font-semibold uppercase tracking-wide ${meta.tagBg}`}>
 {meta.label}
 </span>
 <span className="text-app-micro px-1.5 py-0.5 rounded-full bg-info/80 text-on-brand font-medium">
 proposed
 </span>
 </div>
 {item.description && (
 <p className="text-xs text-muted mt-0.5 line-clamp-1">
 {item.description}
 </p>
 )}
 </div>
 </div>
 {hasChildren && (
 <div className="border-l border-dashed border-strong ml-5">
 {item._proposedChildren.map((child: any) => renderProposedNode(child, depth + 1))}
 </div>
 )}
 </div>
 )
 }

 if (loading) {
 return (
 <div className={`flex items-center justify-center py-16 ${className}`}>
 <Loader2 className="w-6 h-6 animate-spin text-muted" />
 <span className="ml-2 text-muted">Loading life map...</span>
 </div>
 )
 }

 if (error) {
 return (
 <div className={`text-center py-12 ${className}`}>
 <AlertTriangle className="w-8 h-8 text-bad mx-auto mb-2" />
 <p className="text-sm text-bad">{error}</p>
 <button onClick={load} className="mt-2 text-xs text-brand hover:underline">Retry</button>
 </div>
 )
 }

 const totalNodes = nodes.length
 const hasProposed = proposedOnlyItems.length > 0

 if (totalNodes === 0 && !hasProposed) {
 return (
 <div className={`text-center py-16 ${className}`}>
 <Star className="w-12 h-12 text-muted/50 mx-auto mb-3" />
 <p className="text-muted text-sm">Your life map is empty</p>
 <p className="text-muted text-xs mt-1">Chat with {BRAND.assistant} to start building it</p>
 </div>
 )
 }

 return (
 <div className={`space-y-2 ${className}`}>
 {/* Header with refresh */}
 <div className="flex items-center justify-between mb-3">
 <div>
 <p className="text-xs text-muted">
 {totalNodes} item{totalNodes !== 1 ? 's' : ''} in your life map
 {hasProposed && (
 <span className="text-info ml-1">
 + {proposedOnlyItems.length} new from this chat
 </span>
 )}
 </p>
 </div>
 <button
 onClick={load}
 className="p-1.5 rounded-lg hover:bg-surface-alt text-muted transition-colors"
 title="Refresh"
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>

 {/* Tree view */}
 <div className="bg-surface border border-soft rounded-lg p-3 space-y-1">
 {treeRoots.map(root => renderTreeNode(root, 0))}
 </div>

 {/* Proposed items (new from this conversation, not yet saved) */}
 {hasProposed && proposedTreeRoots.length > 0 && (
 <div className="mt-3">
 <h4 className="text-xs font-semibold text-info uppercase tracking-wide mb-2">
 New from this conversation
 </h4>
 <div className="bg-surface border border-dashed border-tint-info-edge rounded-lg p-3 space-y-1">
 {proposedTreeRoots.map((root: any) => renderProposedNode(root, 0))}
 </div>
 </div>
 )}
 </div>
 )
}
